import { useContext, useEffect, useState, useRef } from "react";
import { MyContext } from "../../Context/MyProvider";
import {
    LocalUser,
    RemoteUser,
    useIsConnected,
    useJoin,
    useLocalMicrophoneTrack,
    useLocalCameraTrack,
    usePublish,
    useRemoteUsers,
} from "agora-rtc-react";
import { socket } from "../../socket";
import secureLocalStorage from "react-secure-storage";
import { useTranslation } from "react-i18next";

// ==========================================
// 1. Agora Video Calling Sub-component
// ==========================================
const AgoraVideoCall = ({ CallData, agoraAppId, token, channel, micOn, cameraOn, BaseUrl }) => {
    const { t } = useTranslation();
    const isConnected = useIsConnected();
    const remoteUsers = useRemoteUsers();

    const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);
    const { localCameraTrack } = useLocalCameraTrack(cameraOn);

    usePublish([localMicrophoneTrack, localCameraTrack]);
    useJoin({ appid: agoraAppId, channel, token }, !!channel);

    useEffect(() => {
        return () => {
            if (localMicrophoneTrack) {
                localMicrophoneTrack.stop();
                localMicrophoneTrack.close();
            }
            if (localCameraTrack) {
                localCameraTrack.stop();
                localCameraTrack.close();
            }
        };
    }, [localMicrophoneTrack, localCameraTrack]);

    return (
        <div className="w-full h-full relative flex items-center justify-center">
            {/* Local Video in Corner */}
            <div className="absolute top-4 right-4 w-[110px] h-[150px] rounded-[10px] overflow-hidden border-2 border-indigo-500 z-30 shadow-lg">
                <LocalUser
                    audioTrack={localMicrophoneTrack}
                    videoTrack={localCameraTrack}
                    cameraOn={cameraOn}
                    micOn={micOn}
                    cover={BaseUrl + CallData?.User_image}
                    className="w-full h-full object-cover"
                />
            </div>

            {/* Remote Videos */}
            <div className="w-full h-full bg-black/80 flex items-center justify-center rounded-[15px] overflow-hidden">
                {remoteUsers.length > 0 ? (
                    remoteUsers.map((user) => (
                        <div className="w-full h-full relative" key={user.uid}>
                            <RemoteUser
                                user={user}
                                cover={BaseUrl + CallData?.Astrologer_image}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    ))
                ) : (
                    <div className="text-center text-gray-400 z-10">
                        <div className="mx-auto w-[100px] h-[100px] rounded-full overflow-hidden border-2 border-gray-700 animate-pulse mb-3">
                            <img src={BaseUrl + CallData?.Astrologer_image} className="w-full h-full object-cover" alt={t("Astrologer")} />
                        </div>
                        <p className="text-sm">{t("Waiting for remote user to join...")}</p>
                        <p className="text-xs text-gray-500 mt-1">({t("Agora Engine")})</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==========================================
// 2. Native WebRTC Video Sub-component
// ==========================================
const WebRtcVideoCall = ({ CallData, credentials, micOn, cameraOn, BaseUrl }) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState("Initializing WebRTC P2P...");
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const localStreamRef = useRef(null);
    const peerConnectionRef = useRef(null);

    useEffect(() => {
        let isUnmounted = false;
        const appoint_id = CallData?.appoint_id;
        const currentUserId = secureLocalStorage.getItem("Userid");
        const stunUrl = credentials?.stunUrl || "stun:stun.l.google.com:19302";
        const turnUrl = credentials?.turnUrl;
        const turnUsername = credentials?.turnUsername;
        const turnPassword = credentials?.turnPassword;

        const iceServers = [{ urls: stunUrl }];
        if (turnUrl) {
            iceServers.push({
                urls: turnUrl,
                username: turnUsername,
                credential: turnPassword
            });
        }

        const pc = new RTCPeerConnection({ iceServers });
        peerConnectionRef.current = pc;

        // Fetch user media (audio + video)
        navigator.mediaDevices.getUserMedia({ audio: true, video: true })
            .then((stream) => {
                if (isUnmounted) {
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }
                localStreamRef.current = stream;
                if (localVideoRef.current) {
                    localVideoRef.current.srcObject = stream;
                }

                // Sync initial mic and camera states
                stream.getAudioTracks().forEach(track => track.enabled = micOn);
                stream.getVideoTracks().forEach(track => track.enabled = cameraOn);

                stream.getTracks().forEach((track) => {
                    pc.addTrack(track, stream);
                });

                // User initiates offer creation
                const isUser = String(CallData?.user_id) === String(currentUserId);
                if (isUser) {
                    pc.createOffer()
                        .then((offer) => {
                            if (isUnmounted) return;
                            return pc.setLocalDescription(offer);
                        })
                        .then(() => {
                            if (isUnmounted) return;
                            socket.emit("webrtc_signal", {
                                appoint_id,
                                sdp: pc.localDescription,
                                type: "offer"
                            });
                        });
                }
            })
            .catch((err) => {
                console.error("Camera/Microphone access error:", err);
                if (!isUnmounted) {
                    setStatus("Access Denied (Camera/Mic)");
                }
            });

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                socket.emit("webrtc_signal", {
                    appoint_id,
                    candidate: event.candidate,
                    type: "candidate"
                });
            }
        };

        pc.onconnectionstatechange = () => {
            if (pc.connectionState === "connected") {
                setStatus("Connected");
            } else if (pc.connectionState === "failed") {
                setStatus("Connection Failed");
            }
        };

        pc.ontrack = (event) => {
            if (remoteVideoRef.current) {
                remoteVideoRef.current.srcObject = event.streams[0];
            }
        };

        const handleSignal = (data) => {
            if (data.type === "offer" && pc.signalingState !== "stable") {
                pc.setRemoteDescription(new RTCSessionDescription(data.sdp))
                    .then(() => pc.createAnswer())
                    .then((answer) => pc.setLocalDescription(answer))
                    .then(() => {
                        socket.emit("webrtc_signal", {
                            appoint_id,
                            sdp: pc.localDescription,
                            type: "answer"
                        });
                    });
            } else if (data.type === "answer") {
                pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
            } else if (data.type === "candidate" && data.candidate) {
                pc.addIceCandidate(new RTCIceCandidate(data.candidate))
                    .catch(err => console.error("Error adding ice candidate:", err));
            }
        };

        socket.on(`webrtc_signal_${appoint_id}`, handleSignal);

        return () => {
            isUnmounted = true;
            socket.off(`webrtc_signal_${appoint_id}`, handleSignal);
            if (localStreamRef.current) {
                try {
                    localStreamRef.current.getTracks().forEach(track => track.stop());
                } catch (e) {
                    console.error("Error stopping video tracks in cleanup:", e);
                }
            }
            if (peerConnectionRef.current) {
                try {
                    peerConnectionRef.current.close();
                } catch (e) {
                    console.error("Error closing peer connection in cleanup:", e);
                }
            }
        };
    }, []);

    // Sync state updates
    useEffect(() => {
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach(track => {
                track.enabled = micOn;
            });
        }
    }, [micOn]);

    useEffect(() => {
        if (localStreamRef.current) {
            localStreamRef.current.getVideoTracks().forEach(track => {
                track.enabled = cameraOn;
            });
        }
    }, [cameraOn]);

    return (
        <div className="w-full h-full relative flex items-center justify-center">
            {/* Local Video */}
            <div className="absolute top-4 right-4 w-[110px] h-[150px] rounded-[10px] overflow-hidden border-2 border-indigo-500 z-30 shadow-lg bg-black">
                <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>

            {/* Remote Video */}
            <div className="w-full h-full bg-black/80 flex items-center justify-center rounded-[15px] overflow-hidden relative">
                <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
                {status !== "Connected" && (
                    <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-gray-400 p-4 text-center z-10">
                        <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-700 animate-pulse mb-3">
                            <img src={BaseUrl + CallData?.Astrologer_image} className="w-full h-full object-cover" alt={t("Remote avatar")} />
                        </div>
                        <p className="text-sm font-semibold">{t(status)}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==========================================
// 3. Main Call Screen Component
// ==========================================
const Videocall = ({ CallData, setIsVideoCall }) => {
    const { t } = useTranslation();
    const {
        BaseUrl,
        agoraAppId,
        HandleCallApi,
        setIsToast,
        setToastMsg,
        setIsRating,
        setRatingData,
    } = useContext(MyContext);

    const [channel, setChannel] = useState("");
    const [calling, setCalling] = useState(false);
    const [micOn, setMic] = useState(true);
    const [cameraOn, setCamera] = useState(true);
    const [chatTime, setChatTime] = useState("00:00");
    const [running, setRunning] = useState(false);
    const [apiCredentials, setApiCredentials] = useState(null);

    const dailyContainerRef = useRef(null);
    const [dailyCall, setDailyCall] = useState(null);

    const credentials = CallData?.credentials || apiCredentials;
    const provider = credentials?.provider || "agora";

    useEffect(() => {
        if (!CallData) return;

        setChannel(`${CallData.astrologer_id}_${CallData.user_id}`);
        setCalling(true);
        getChatTime();
    }, [CallData]);

    useEffect(() => {
        const userId = secureLocalStorage.getItem("Userid");

        const onChatTimeOver = () => {
            endCall(false);
        };

        const handleIframeMessage = (event) => {
            if (event.data && event.data.type === 'end_call') {
                console.log("Received end_call message from iframe. Ending call...");
                endCall(true);
            }
        };

        socket.on(`chat_time_over_user${userId}`, onChatTimeOver);
        window.addEventListener('message', handleIframeMessage);

        return () => {
            socket.off(`chat_time_over_user${userId}`, onChatTimeOver);
            window.removeEventListener('message', handleIframeMessage);
        };
    }, [CallData]);

    const getChatTime = async () => {
        const res = await HandleCallApi(
            "Post",
            "chat/audio_video_call",
            { appointment_id: CallData?.appoint_id }
        );

        setChatTime(res?.data?.chat_time?.chat_time_remaining || "00:00");
        const activeCreds = CallData?.credentials || res?.data?.credentials;
        const activeProvider = activeCreds?.provider || "agora";

        if (["daily", "mirotalk", "100ms", "janus"].includes(activeProvider) && !activeCreds?.roomUrl) {
            setIsToast(true);
            setToastMsg(t("Failed to load call room credentials. Ending session..."));
            endCall(false);
            return;
        }

        if (res?.data?.credentials) {
            setApiCredentials(res.data.credentials);
        }
        setRunning(true);
    };

    useEffect(() => {
        if (!running) return;

        const interval = setInterval(() => {
            setChatTime((prev) => countdown(prev));
        }, 1000);

        return () => clearInterval(interval);
    }, [running]);

    useEffect(() => {
        if (calling && provider === "daily" && credentials?.roomUrl && dailyContainerRef.current) {
            if (dailyCall) {
                try { dailyCall.destroy(); } catch (e) {}
            }

            const callFrame = window.DailyIframe.createFrame(dailyContainerRef.current, {
                iframeStyle: {
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '15px'
                },
                showLeaveButton: true
            });

            callFrame.on('error', (e) => {
                console.error("Daily.co error event:", e);
                setIsToast(true);
                setToastMsg(t("Call connection error. Ending session..."));
                try { callFrame.destroy(); } catch (err) {}
                endCall(false);
            });

            callFrame.on('left-meeting', () => {
                try { callFrame.destroy(); } catch (err) {}
                endCall(true);
            });

            callFrame.join({ url: credentials.roomUrl }).catch(err => {
                console.error("Daily.co join failed:", err);
            });
            setDailyCall(callFrame);

            return () => {
                try { callFrame.destroy(); } catch (e) {}
            };
        }
    }, [calling, provider, credentials?.roomUrl]);

    const countdown = (time) => {
        const [m, s] = time.split(":").map(Number);
        let total = m * 60 + s - 1;
        if (total <= 0) return "00:00";
        return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(
            total % 60
        ).padStart(2, "0")}`;
    };

    const endCall = async (showRating = true) => {
        await HandleCallApi("Post", "astro/end_chat", {
            appoint_id: CallData?.appoint_id,
        });

        socket.emit("end_chat", { appoint_id: CallData?.appoint_id });

        setCalling(false);
        setCamera(false);
        setMic(false);
        setIsVideoCall(false);

        setIsToast(true);
        setToastMsg(t("Call ended"));

        if (showRating) {
            setIsRating(true);
            setRatingData({
                appointment_id: CallData?.appoint_id,
                astrologer_detail: {
                    image: CallData?.Astrologer_image,
                    name: CallData?.Astrologer_name,
                },
                astrologer_id: CallData?.astrologer_id,
                conversation_type: CallData?.appointment_type,
            });
        }
    };

    const toggleMic = () => {
        setMic(!micOn);
    };

    return (
        <div className="w-full h-full relative bg-gray-900 rounded-[20px] shadow-2xl overflow-hidden border border-gray-800 flex flex-col justify-between p-4">
            {/* Header overlay */}
            <div className="absolute top-4 left-4 z-20 bg-black/60 backdrop-blur-md border border-gray-800/80 px-4 py-2 rounded-[12px] flex items-center gap-3">
                <div className="w-8 h-8 rounded-full overflow-hidden border border-indigo-500/40">
                    <img src={BaseUrl + CallData?.Astrologer_image} className="w-full h-full object-cover" alt={t("Astro avatar")} />
                </div>
                <div>
                    <h1 className="text-white text-sm font-semibold tracking-wide">
                        {CallData?.Astrologer_name}
                    </h1>
                    <p className="text-indigo-400 font-mono text-xs font-semibold">{chatTime}</p>
                </div>
            </div>

            {/* Embedded Iframe for Daily.co / MiroTalk / 100ms */}
            {calling && (provider === "daily" || provider === "mirotalk" || provider === "100ms" || provider === "janus") ? (
                <div className="w-full h-[85%] flex flex-col items-center justify-center z-10 relative mt-16">
                    <div className="w-full h-full rounded-[15px] overflow-hidden border border-gray-800 bg-black">
                        {provider === "daily" ? (
                            <div ref={dailyContainerRef} style={{ width: "100%", height: "100%" }} />
                        ) : (
                            <iframe
                                src={(provider === "mirotalk" || provider === "100ms" || provider === "janus") ? credentials?.roomUrl : `https://sandbox.100ms.live/meeting/${credentials?.roomId || "sandbox"}`}
                                allow="camera; microphone; autoplay; display-capture"
                                className="w-full h-full border-0"
                                title={t("Embedded Video Node")}
                            />
                        )}
                    </div>
                    <div className="mt-4 flex w-full justify-center">
                        <button
                            onClick={() => endCall(true)}
                            className="bg-red-600 hover:bg-red-700 hover:scale-105 active:scale-95 duration-200 px-6 py-2.5 rounded-full font-semibold flex items-center gap-2 shadow-lg text-white"
                        >
                            <svg className="fill-current w-5 h-5" viewBox="0 0 24 24"><path d="M16.5521 21.752c-.459 0-.921-.062-1.376-.187-6.165-1.692-11.046-6.57-12.739-12.731C2.051 7.433 2.262 5.973 3.032 4.725c.773-1.254 2.044-2.139 3.487-2.427.96-.192 1.909.236 2.396 1.057l1.563 2.64c.759 1.282.382 2.933-.859 3.759l-1.131.752c1.057 2.17 2.835 3.953 4.996 5.009l.762-1.137c.831-1.239 2.482-1.61 3.763-.847l2.643 1.576c.818.488 1.241 1.443 1.055 2.375-.288 1.443-1.173 2.714-2.426 3.487-.842.518-1.779.783-2.729.783z"/></svg>
                            {t("End Video Session")}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="w-full h-full relative flex flex-col justify-between">
                    {/* Visual stream block */}
                    <div className="w-full h-[85%] rounded-[15px] overflow-hidden border border-gray-800 bg-black/40 mt-14">
                        {calling && (
                            provider === "agora" ? (
                                <AgoraVideoCall
                                    CallData={CallData}
                                    agoraAppId={credentials?.appId || agoraAppId}
                                    token={credentials?.token}
                                    channel={channel}
                                    micOn={micOn}
                                    cameraOn={cameraOn}
                                    BaseUrl={BaseUrl}
                                />
                            ) : (
                                <WebRtcVideoCall
                                    CallData={CallData}
                                    credentials={credentials}
                                    micOn={micOn}
                                    cameraOn={cameraOn}
                                    BaseUrl={BaseUrl}
                                />
                            )
                        )}
                    </div>

                    {/* Bottom overlay controls */}
                    <div className="flex gap-6 justify-center items-center py-4 z-20">
                        <button
                            onClick={() => setCamera(!cameraOn)}
                            className={`p-4 rounded-full shadow-lg border duration-300 transform active:scale-95 ${
                                cameraOn
                                ? "bg-gray-800/80 border-gray-700 hover:bg-gray-700 text-white"
                                : "bg-red-500/20 border-red-500/40 hover:bg-red-500/30 text-red-400"
                            }`}
                        >
                            {cameraOn ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 6.5l-4 4V7c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-3.5l4 4v-11zM15 17H3V7h12v10z" fill="currentColor" />
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 6.5l-4 4V7c0-1.1-.9-2-2-2H5.83l10 10H15c1.1 0 2-.9 2-2v-3.5l4 4V6.5zM3.56 2.56L2.28 3.84l2.45 2.45C3.76 6.58 3 7.69 3 9v8c0 1.1.9 2 2 2h11c.78 0 1.48-.38 1.94-.96l2.1 2.1 1.28-1.28L3.56 2.56zm1.44 14.44V9c0-.46.12-.88.31-1.25l7.94 7.94c-.37.19-.79.31-1.25.31H5zm14-11V6.5l2.25-2.25L24 5.5v11c0 .41-.12.78-.32 1.1l-4.68-4.68V9h-3.5l-2-2H19z" fill="currentColor" />
                                </svg>
                            )}
                        </button>

                        <button
                            onClick={toggleMic}
                            className={`p-4 rounded-full shadow-lg border duration-300 transform active:scale-95 ${
                                micOn
                                ? "bg-gray-800/80 border-gray-700 hover:bg-gray-700 text-white"
                                : "bg-red-500/20 border-red-500/40 hover:bg-red-500/30 text-red-400"
                            }`}
                        >
                            {micOn ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" fill="currentColor" />
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-2.7 3.05l-1.2-1.2C15.7 12.08 16 11.57 16 11V5c0-2.21-1.79-4-4-4S8 2.79 8 5v1.88l8.3 8.3c.27-.63.43-1.32.43-2.13zm-10.6-9L4.27 3 3 4.27l6.01 6.01V11c0 2.21 1.79 4 4 4 .78 0 1.5-.23 2.11-.61l2.9 2.9C16.42 18.3 14.82 19 13 19v2h-2v-2.05c-3.79-.49-6.8-3.75-6.8-7.7H2.5c0 4.42 3.28 8.09 7.5 8.7V23h4v-1.3c1.78-.26 3.43-.99 4.79-2.09l1.89 1.89L24 20.22 5.7 1.95z" fill="currentColor" />
                                </svg>
                            )}
                        </button>

                        <button
                            onClick={() => endCall(true)}
                            className="bg-red-600 hover:bg-red-700 hover:scale-105 active:scale-95 duration-300 p-4 rounded-full shadow-lg border border-red-500/20 text-white"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16.5521 21.752c-.459 0-.921-.062-1.376-.187-6.165-1.692-11.046-6.57-12.739-12.731C2.051 7.433 2.262 5.973 3.032 4.725c.773-1.254 2.044-2.139 3.487-2.427.96-.192 1.909.236 2.396 1.057l1.563 2.64c.759 1.282.382 2.933-.859 3.759l-1.131.752c1.057 2.17 2.835 3.953 4.996 5.009l.762-1.137c.831-1.239 2.482-1.61 3.763-.847l2.643 1.576c.818.488 1.241 1.443 1.055 2.375-.288 1.443-1.173 2.714-2.426 3.487-.842.518-1.779.783-2.729.783z" fill="currentColor" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Videocall;
